import {useCallback, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {Copy, KeyRound, ShieldAlert} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {DatePicker} from '@/components/shared/DatePicker'
import {Input} from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {DialogForm} from '@/components/shared/DialogForm'
import {FormField} from '@/components/shared/FormField'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {useI18n} from '@/i18n'
import {ApiError} from '@/api/client'
import {IamErrorCode} from '@/api/error-codes'
import {AppDate, AppLocalDate} from '@/lib/date-utils'
import {useMintApiKey} from '@/features/service-accounts/hooks/useServiceAccounts'
import type {ApiKeyCreateRequest, ApiKeyMint} from '@/types/service-account'

const DEFAULT_RATE_LIMIT = 600

interface MintApiKeyDialogProps {
    serviceAccountId: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function MintApiKeyDialog({serviceAccountId, open, onOpenChange}: MintApiKeyDialogProps) {
    const i18n = useI18n()
    const mintMutation = useMintApiKey(serviceAccountId)

    const [name, setName] = useState('')
    const [expiresAtDate, setExpiresAtDate] = useState(() => AppLocalDate.maxFromToday(365))
    const [rateLimit, setRateLimit] = useState('')
    const [nameError, setNameError] = useState<string | null>(null)
    const [expiryError, setExpiryError] = useState<string | null>(null)
    const [rateLimitError, setRateLimitError] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [rawKey, setRawKey] = useState<string | null>(null)
    const rawKeyRef = useRef<string | null>(null)
    const [copyDone, setCopyDone] = useState(false)

    // WHY: keep ref in sync so the unmount/cleanup path can null both —
    // a stale closure on the state setter cannot reach into the GC'd cell.
    rawKeyRef.current = rawKey

    const isSecureClipboard = typeof window !== 'undefined'
        && window.isSecureContext === true
        && typeof navigator !== 'undefined'
        && typeof navigator.clipboard?.writeText === 'function'

    const resetForm = useCallback(() => {
        setName('')
        setExpiresAtDate(AppLocalDate.maxFromToday(365))
        setRateLimit('')
        setNameError(null)
        setExpiryError(null)
        setRateLimitError(null)
        setSubmitError(null)
        setCopyDone(false)
    }, [])

    const dereference = useCallback(() => {
        setRawKey(null)
        rawKeyRef.current = null
        setCopyDone(false)
    }, [])

    const closeAndReset = useCallback(() => {
        dereference()
        resetForm()
        mintMutation.reset()
        onOpenChange(false)
    }, [dereference, resetForm, mintMutation, onOpenChange])

    // WHY: SEC review #8 — route-change must dereference the raw key. If the
    // user navigates away while the disclosure panel is open, force-close so
    // the React state holding the secret is released.
    const location = useLocation()
    const previousPathRef = useRef(location.pathname)
    useEffect(() => {
        if (previousPathRef.current !== location.pathname && open) {
            previousPathRef.current = location.pathname
            closeAndReset()
            return
        }
        previousPathRef.current = location.pathname
    }, [location.pathname, open, closeAndReset])

    // WHY: belt-and-braces deref — if the dialog unmounts while the raw key
    // is still in state, null both the state cell AND the ref so no closure
    // can recover the value (spec §4 rule S5).
    useEffect(() => {
        return () => {
            rawKeyRef.current = null
        }
    }, [])

    const handleOpenChange = useCallback((next: boolean) => {
        if (next) {
            onOpenChange(true)
            return
        }
        if (rawKey !== null) {
            // WHY: confirmation gate while disclosure panel is visible —
            // prevents an accidental click-outside / Escape from losing the
            // key (spec §8 "I've saved it" flow).
            const confirmed = window.confirm(i18n.apiKeys.mintDialog.closeConfirm as string)
            if (!confirmed) return
        }
        closeAndReset()
    }, [rawKey, closeAndReset, onOpenChange, i18n.apiKeys.mintDialog.closeConfirm])

    const validate = useCallback((): boolean => {
        let ok = true
        const trimmed = name.trim()
        if (!trimmed) {
            setNameError(i18n.apiKeys.mintDialog.nameRequired as string)
            ok = false
        } else if (trimmed.length > 100) {
            setNameError(i18n.apiKeys.mintDialog.nameTooLong as string)
            ok = false
        } else {
            setNameError(null)
        }
        if (!expiresAtDate || expiresAtDate < AppLocalDate.today()) {
            setExpiryError(i18n.apiKeys.mintDialog.expiresAtInPast as string)
            ok = false
        } else {
            setExpiryError(null)
        }
        if (rateLimit.trim().length > 0) {
            const parsed = Number(rateLimit)
            if (!Number.isInteger(parsed) || parsed < 1) {
                setRateLimitError(i18n.apiKeys.mintDialog.rateLimitInvalid as string)
                ok = false
            } else {
                setRateLimitError(null)
            }
        } else {
            setRateLimitError(null)
        }
        return ok
    }, [name, expiresAtDate, rateLimit, i18n.apiKeys.mintDialog])

    const submit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setSubmitError(null)
        if (!validate()) return
        // eslint-disable-next-line i18next/no-literal-string -- ISO instant suffix is a wire format, not a user-visible string
        const expiresAtIso = `${expiresAtDate}T23:59:59.000Z`
        const req: ApiKeyCreateRequest = {
            name: name.trim(),
            expiresAt: expiresAtIso,
            rateLimitPerMinute: rateLimit.trim().length > 0 ? Number(rateLimit) : null,
        }
        mintMutation.mutate(req, {
            onSuccess: (response: ApiKeyMint) => {
                setRawKey(response.key)
            },
            onError: (err) => {
                if (err instanceof ApiError) {
                    if (err.code === IamErrorCode.AU0022_EXPIRES_AT_IN_PAST) {
                        setExpiryError(i18n.apiKeys.mintDialog.expiresAtInPast as string)
                        return
                    }
                    if (err.code === IamErrorCode.AU0023_SERVICE_ACCOUNT_INACTIVE) {
                        setSubmitError(i18n.apiKeys.mintDialog.inactiveError as string)
                        return
                    }
                }
                setSubmitError(err.message)
            },
        })
    }, [validate, expiresAtDate, name, rateLimit, mintMutation, i18n.apiKeys.mintDialog])

    const onCopy = useCallback(async () => {
        if (!rawKey || !isSecureClipboard) return
        await navigator.clipboard.writeText(rawKey)
        setCopyDone(true)
    }, [rawKey, isSecureClipboard])

    const showDisclosurePanel = rawKey !== null

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                onInteractOutside={(e) => {
                    if (rawKey !== null) {
                        e.preventDefault()
                        handleOpenChange(false)
                    } else {
                        e.preventDefault()
                    }
                }}
                onEscapeKeyDown={(e) => {
                    if (rawKey !== null) {
                        e.preventDefault()
                        handleOpenChange(false)
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle data-testid="mint-dialog-title">
                        <InlineRow gap="sm">
                            <Icon icon={KeyRound}/>
                            {showDisclosurePanel
                                ? i18n.apiKeys.mintDialog.disclosurePanelTitle
                                : i18n.apiKeys.mintDialog.title}
                        </InlineRow>
                    </DialogTitle>
                    {!showDisclosurePanel && (
                        <DialogDescription>
                            {i18n.apiKeys.mintDialog.description}
                        </DialogDescription>
                    )}
                </DialogHeader>
                {showDisclosurePanel ? (
                    <div className="space-y-4">
                        <InfoBanner variant="destructive" icon={ShieldAlert} data-testid="mint-warning-banner">
                            {i18n.apiKeys.mintDialog.warningBanner}
                        </InfoBanner>
                        <FormField
                            label={i18n.apiKeys.mintDialog.keyLabel as string}
                            htmlFor="mint-raw-key"
                        >
                            <InlineRow gap="sm">
                                <Input
                                    id="mint-raw-key"
                                    data-testid="mint-raw-key-input"
                                    type="text"
                                    readOnly
                                    value={rawKey ?? ''}
                                    className="font-mono"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onCopy}
                                    disabled={!isSecureClipboard}
                                    data-testid="btn-copy-raw-key"
                                >
                                    <Icon icon={Copy} className="mr-2"/>
                                    {copyDone
                                        ? i18n.apiKeys.mintDialog.copyDone
                                        : i18n.apiKeys.mintDialog.copyButton}
                                </Button>
                            </InlineRow>
                        </FormField>
                        {!isSecureClipboard && (
                            <MutedText size="sm" data-testid="mint-copy-disabled-message">
                                {i18n.apiKeys.mintDialog.copyDisabledMessage}
                            </MutedText>
                        )}
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={closeAndReset}
                                data-testid="btn-mint-saved-close"
                            >
                                {i18n.apiKeys.mintDialog.closeButton}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <DialogForm onSubmit={submit} data-testid="mint-form">
                        {submitError && (
                            <InfoBanner variant="destructive" data-testid="mint-submit-error">
                                {submitError}
                            </InfoBanner>
                        )}
                        <FormField
                            label={i18n.apiKeys.mintDialog.nameLabel as string}
                            htmlFor="mint-name"
                            required
                            error={nameError ?? undefined}
                        >
                            <Input
                                id="mint-name"
                                data-testid="mint-input-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={i18n.apiKeys.mintDialog.namePlaceholder as string}
                                maxLength={100}
                                autoFocus
                            />
                        </FormField>
                        <FormField
                            label={i18n.apiKeys.mintDialog.expiryLabel as string}
                            htmlFor="mint-expiry"
                            required
                            error={expiryError ?? undefined}
                        >
                            <DatePicker
                                testId="mint-input-expiry"
                                value={expiresAtDate}
                                onChange={(iso) => setExpiresAtDate(iso)}
                                ariaLabel={i18n.apiKeys.mintDialog.expiryLabel as string}
                                minDate={AppDate.now()}
                            />
                        </FormField>
                        <FormField
                            label={i18n.apiKeys.mintDialog.rateLimitLabel as string}
                            htmlFor="mint-rate-limit"
                            error={rateLimitError ?? undefined}
                        >
                            <Input
                                id="mint-rate-limit"
                                data-testid="mint-input-rate-limit"
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                value={rateLimit}
                                onChange={(e) => setRateLimit(e.target.value)}
                                placeholder={i18n.apiKeys.mintDialog.rateLimitPlaceholder({value: DEFAULT_RATE_LIMIT}) as string}
                            />
                        </FormField>
                        <SaveCancelFooter
                            testIdPrefix="mint"
                            onCancel={closeAndReset}
                            saving={mintMutation.isPending}
                            saveLabel={i18n.apiKeys.mintDialog.submit as string}
                            savingLabel={i18n.apiKeys.mintDialog.submitting as string}
                        />
                    </DialogForm>
                )}
            </DialogContent>
        </Dialog>
    )
}
